import * as React from 'react';
import { Auth } from '../services';
import { BaseRouteProps } from '../interfaces/props';
import { LoadingIndicator } from './loadingIndicator/LoadingIndicator';
import { PrivacyPolicy } from './PrivacyPolicy';

import './Landing.css';
import { LoginResponse } from '../enums';
import { MfaCodeEntry } from './MfaCodeEntry';

enum ScreenState {
    Login,
    ResetPassword,
    ChangePassword,
    MfaCodeEntry,
}
interface LandingState {
    screen: ScreenState;
    email: string;
    emailError: boolean;
    password: string;
    passwordError: boolean;
    resetEmail: string;
    resetEmailError: boolean;
    changeEmail: string;
    changeEmailError: boolean;
    changePassword: string;
    changePasswordError: boolean;
    changePasswordConfirm: string;
    changePasswordConfirmError: boolean;
    changePasswordEmail: string;
    changePasswordToken: string;
    errorMessage: string;
    confirmationMessage: string;
    busy: boolean;
    pageLoadTime: number;
}

interface LandingProps extends BaseRouteProps<{}> { }

export class Landing extends React.Component<LandingProps, LandingState> {
    auth: Auth = new Auth(this.props.history);

    constructor(props: LandingProps) {
        super(props);

        const params = new URLSearchParams(props.location.search);
        const email = params.get('email') || '';
        const token = params.get('token') || '';
        const mode = params.get('mode') || '';

        let screen = ScreenState.Login;
        if (email && token) {
            screen = ScreenState.ChangePassword;
        }
        if (mode === 'mfa') {
            screen = ScreenState.MfaCodeEntry;
        }

        this.state = {
            screen: screen,
            email: '',
            emailError: false,
            password: '',
            passwordError: false,
            resetEmail: '',
            resetEmailError: false,
            changeEmail: '',
            changeEmailError: false,
            changePassword: '',
            changePasswordError: false,
            changePasswordConfirm: '',
            changePasswordConfirmError: false,
            changePasswordEmail: email,
            changePasswordToken: token,
            errorMessage: '',
            confirmationMessage: '',
            busy: false,
            pageLoadTime: Date.now(),
        };
    }

    changeEmail = async (e: any) => {
        const value = e.target.value;
        if (!this.ignoringAutofill()) {
            await this.setErrorMessage('');
            await this.setConfirmationMessage('');
        }
        await this.updateState({email: value});
        await this.checkEmail();
    }

    changePasswordResetEmail = async (e: any) => {
        const value = e.target.value;
        if (!this.ignoringAutofill()) {
            await this.setErrorMessage('');
            await this.setConfirmationMessage('');
        }
        await this.updateState({resetEmail: value});
        await this.checkResetEmail();
    }

    changePassword = async (e: any) => {
        const value = e.target.value;
        if (!this.ignoringAutofill()) {
            await this.setErrorMessage('');
            await this.setConfirmationMessage('');
        }
        await this.updateState({password: value});
        await this.checkPassword();
    }

    changeChangeEmail = async (e: any) => {
        const value = e.target.value;
        if (!this.ignoringAutofill()) {
            await this.setErrorMessage('');
            await this.setConfirmationMessage('');
        }
        await this.updateState({changeEmail: value});
        await this.checkChangeEmail();
    }

    changeChangePassword = async (e: any) => {
        const value = e.target.value;
        if (!this.ignoringAutofill()) {
            await this.setErrorMessage('');
            await this.setConfirmationMessage('');
        }
        await this.updateState({changePassword: value});
        await this.checkChangePassword();
        await this.checkChangePasswordConfirm();
    }

    changeChangePasswordConfirm = async (e: any) => {
        const value = e.target.value;
        if (!this.ignoringAutofill()) {
            await this.setErrorMessage('');
            await this.setConfirmationMessage('');
        }
        await this.updateState({changePasswordConfirm: value});
        await this.checkChangePasswordConfirm();
    }

    setInput = (id: string, value: string) => {
        const element = document.getElementById(id) as any;
        if (element !== null) {
            element.value = value;
        }
    }

    switchToResetPassword = async (e?: any) => {
        if (e) {
            e.preventDefault();
        }
        await this.setErrorMessage('');
        await this.setConfirmationMessage('');
        await this.updateState({screen: ScreenState.ResetPassword});
        this.setInput("resetEmail", this.state.resetEmail);
    }

    switchToLogin = async (e?: any) => {
        if (e) {
            e.preventDefault();
        }
        await this.setErrorMessage('');
        await this.setConfirmationMessage('');
        await this.updateState({screen: ScreenState.Login});
        this.setInput("loginEmail", this.state.email);
        this.setInput("loginPassword", this.state.password);
    }

    setBusy = async (): Promise<void> => {
        await this.updateState({ busy: true });
    }
    clearBusy = async (): Promise<void> => {
        await this.updateState({ busy: false, pageLoadTime: Date.now() });
    }

    handleKey = async (e: any): Promise<void> => {
        if (e.keyCode === 13) {
            e.preventDefault();
            if (this.state.screen === ScreenState.ChangePassword) {
                await this.setPassword();
            }
            if (this.state.screen === ScreenState.ResetPassword) {
                await this.resetPassword();
            }
            if (this.state.screen === ScreenState.Login) {
                await this.authenticate();
            }
        }
    }

    public render() {
        const message = (
            <>
                {this.state && this.state.errorMessage && (
                    <div className={"auth-error-container"}>
                        {this.state.errorMessage}
                    </div>
                )}
                {this.state && this.state.confirmationMessage && (
                    <div className={"auth-confirmation-container"}>
                        {this.state.confirmationMessage}
                    </div>
                )}
            </>
        );

        if (this.state.busy) {
            return <LoadingIndicator />;
        }

        return (
            <React.Fragment>
                <div className="landing">
                    <img src="/images/logo.png" />
                {this.state && this.state.screen === ScreenState.MfaCodeEntry &&
                    <MfaCodeEntry
                        message={message}
                        notificationService={this.props.notificationService}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        onError={async () => await this.setErrorMessage(`Invalid authorization code. Please try again.`)}
                        onChange={async () => await this.setErrorMessage(``)}
                    />
                }
                {this.state && this.state.screen === ScreenState.ChangePassword && <div className="auth-container">
                    <div className={"auth-body-topper set-password-topper"} />
                    <div className={"auth-change-password-text"}>
                        Please enter a new password in both fields. Your new password must have:
                    </div>
                    <div className={"auth-change-password-text"}>
                        <ul>
                            <li>8 or more characters</li>
                            <li>1 or more special characters, like: ! “ # $ % & ‘ ( *</li>
                        </ul>
                    </div>
                    {message}
                    <div className={"auth-body set-password-body"}>
                        <div className={"auth-input" + (this.state.changePasswordError ? " auth-error" : "")}>
                            <div className={"box-icon"}>
                                <span className="glyphicon glyphicon-lock" />
                            </div>
                            <input value={this.state.changePassword} onKeyUp={this.handleKey} className={"box-input"} type="password" name="password" placeholder={"New password"} required={true} onChange={this.changeChangePassword} />
                        </div>
                    </div>
                    <div className={"auth-body set-password-body"}>
                        <div className={"auth-input" + (this.state.changePasswordConfirmError ? " auth-error" : "")}>
                            <div className={"box-icon"}>
                                <span className="glyphicon glyphicon-lock" />
                            </div>
                            <input value={this.state.changePasswordConfirm} onKeyUp={this.handleKey} className={"box-input"} type="password" name="confirmPassword" placeholder={"Confirm new password"} required={true} onChange={this.changeChangePasswordConfirm} />
                        </div>
                    </div>
                    <div className="auth-footer" onClick={this.setPassword}>
                        <div className="auth-button">Set password <span className="auth-arrow">></span></div>
                    </div>
                </div>}
                {this.state && this.state.screen === ScreenState.ResetPassword && <div className="auth-container">
                    <div className={"auth-body-topper change-password-topper"} />
                    <div className={"auth-login-link-container align-left"}>
                        <a href="###" className="reset-password-link login-link" onClick={this.switchToLogin}>
                            <span className="auth-arrow auth-back-arrow">{" < "}</span>
                        </a>
                    </div>
                    {message}
                    <div className={"auth-login-text"}>
                        Please enter your email address. We will send you an email to reset your password.
                    </div>
                    <div className="auth-body change-password-body">
                        <div className={"auth-input" + (this.state.resetEmailError ? " auth-error" : "")}>
                            <div className="box-icon">
                                <span className="glyphicon glyphicon-envelope" />
                            </div>
                            <input id="resetEmail" onKeyUp={this.handleKey} className="box-input" type="email" name="email" placeholder="yours@example.com" required={true} onChange={this.changePasswordResetEmail} />
                        </div>
                    </div>
                    <div className="auth-footer" onClick={this.resetPassword}>
                        <div className="auth-button">Reset password <span className="auth-arrow">></span></div>
                    </div>
                </div>}
                {this.state && this.state.screen === ScreenState.Login && <div className="auth-container">
                    <div className={"auth-body-topper"} />
                    {message}
                    <div className="auth-body">
                        <div className={"auth-input" + (this.state.emailError ? " auth-error" : "")}>
                            <div className="box-icon">
                                <span className="glyphicon glyphicon-envelope" />
                            </div>
                            <input id="loginEmail" onKeyUp={this.handleKey} className={"box-input"} type="email" name="email" placeholder="yours@example.com" required={true} onChange={this.changeEmail} />
                        </div>
                        <div className={"auth-input" + (this.state.passwordError ? " auth-error" : "")}>
                            <div className="box-icon">
                                <span className="glyphicon glyphicon-lock" />
                            </div>
                            <input id="loginPassword" onKeyUp={this.handleKey} className={"box-input"} type="password" name="password" placeholder="your password" required={true} onChange={this.changePassword} />
                        </div>
                        <div className="auth-link-container">
                            <a href="###" className="reset-password-link" onClick={this.switchToResetPassword}>Don't remember your password?</a>
                        </div>
                    </div>
                    <div className="auth-footer" onClick={this.authenticate}>
                        <div className="auth-button">Log in <span className="auth-arrow">></span></div>
                    </div>
                </div>}
                <div className="small">© Pondera Technologies LLC {(new Date()).getFullYear()}</div>
                <div className="privacy-policy-container">
                    <PrivacyPolicy />
                </div>
            </div>
        </React.Fragment>);
    }

    private updateState = async (toMerge: object) => {
        await this.setState((previous, props) => {return {...previous, ...toMerge};});
    }

    private ignoringAutofill = (): boolean => {
        return Date.now() - this.state.pageLoadTime < 500;
    }

    private setErrorMessage = async (message: string) => {
        await this.updateState({errorMessage: message});
    }
    private setConfirmationMessage = async (message: string) => {
        await this.updateState({confirmationMessage: message});
    }
    private setEmailError = async (val: boolean) => {
        await this.updateState({emailError: val});
    }
    private setPasswordError = async (val: boolean) => {
        await this.updateState({passwordError: val});
    }
    private setResetPasswordEmailError = async (val: boolean) => {
        await this.updateState({resetEmailError: val});
    }
    private setChangeEmailError = async (val: boolean) => {
        await this.updateState({changeEmailError: val});
    }
    private setChangePasswordError = async (val: boolean) => {
        await this.updateState({changePasswordError: val});
    }
    private setChangePasswordConfirmError = async (val: boolean) => {
        await this.updateState({changePasswordConfirmError: val});
    }

    private checkEmailField = async (setFunc: Function, email: string): Promise<boolean> => {
        const re: RegExp = /\S+@\S+\.\S+/;
        if (!re.test(email)) {
            await setFunc(true);
            return false;
        }
        return true;
    }
    private checkField = async (setFunc: Function, val: string): Promise<boolean> => {
        await setFunc(false);
        if (!val) {
            await setFunc(true);
            return false;
        }
        return true;
    }
    private checkEmail = async (): Promise<boolean> => {
        let passed: boolean = await this.checkField(this.setEmailError, this.state.email);
        return await this.checkEmailField(this.setEmailError, this.state.email) && passed;
    }
    private checkPassword = async (): Promise<boolean> => {
        return await this.checkField(this.setPasswordError, this.state.password);
    }
    private checkResetEmail = async (): Promise<boolean> => {
        let passed: boolean = await this.checkField(this.setResetPasswordEmailError, this.state.resetEmail);
        return await this.checkEmailField(this.setResetPasswordEmailError, this.state.resetEmail) && passed;
    }
    private checkChangeEmail = async(): Promise<boolean> => {
        let passed: boolean = await this.checkField(this.setChangeEmailError, this.state.changeEmail);
        return await this.checkEmailField(this.setChangeEmailError, this.state.email) && passed;
    }
    private checkChangePassword = async (): Promise<boolean> => {
        if (!this.state.changePassword || this.state.changePassword.match(/^ *$/) !== null) {
            await this.setErrorMessage('Password may not be empty.');
            return false;
        }
        if (this.state.changePassword.length < 8) {
            await this.setErrorMessage('Password must contain 8 or more characters and 1 or more special character.');
            return false;
        }
        if (this.state.changePassword.length > 30) {
            await this.setErrorMessage('Password must contain 30 or fewer characters.');
            return false;
        }
        if (!/[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(this.state.changePassword)) {
            await this.setErrorMessage(`Password must contain a special character.`);
            return false;
        }
        if (!/[0123456789]/.test(this.state.changePassword)) {
            await this.setErrorMessage(`Password must contain a number.`);
            return false;
        }
        return await this.checkField(this.setChangePasswordError, this.state.changePassword);
    }
    private checkChangePasswordConfirm = async (): Promise<boolean> => {
        let passed = await this.checkField(this.setChangePasswordConfirmError, this.state.changePasswordConfirm);
        if (this.state.changePasswordConfirm.length > 0 && this.state.changePasswordConfirm !== this.state.changePassword) {
            await this.setErrorMessage('Passwords must match.');
            await this.setChangePasswordConfirmError(true);
            passed = false;
        }
        return passed;
    }
    private checkAll = async (): Promise<boolean> => {
        if (this.state.screen === ScreenState.Login) {
            let passed: boolean = await this.checkEmail();
            return await this.checkPassword() && passed;
        }
        if (this.state.screen === ScreenState.ResetPassword) {
            return await this.checkResetEmail();
        }
        if (this.state.screen === ScreenState.ChangePassword) {
            let passed: boolean = await this.checkChangePassword();
            return await this.checkChangePasswordConfirm() && passed;
        }
        return true;
    }

    private authenticate = async () => {
        try {
            await this.setBusy();
            await this.setErrorMessage('');
            if (!await this.checkAll()) {
                throw('checkAll() failed');
            }
            const response = await this.auth.authenticateWithEmailAndPassword(this.state.email, this.state.password);
            if (response === LoginResponse.Incorrect) {
               await this.setErrorMessage('Wrong email or password.');
            }
            if (response === LoginResponse.Locked) {
                await this.setErrorMessage('Login locked after too many failed attempts.');
            }
            if (response === LoginResponse.MfaCodeRequired) {
                await this.updateState({screen: ScreenState.MfaCodeEntry});
            }
        } catch(e) {
            console.error(`bad login: ${e}`);
        }
        await this.clearBusy();
    }

    private resetPassword = async () => {
        try {
            await this.setBusy();
            await this.setConfirmationMessage('');
            if (!await this.checkAll()) {
                throw('checkAll() failed');
            }
            await this.auth.resetPassword(this.state.resetEmail);
            await this.switchToLogin();
            await this.setConfirmationMessage('We\'ve just sent you an email to reset your password.');
        } catch(e) {
            console.error(`can not reset password: ${e}`);
        }
        await this.clearBusy();
    }

    private setPassword = async () => {
        try {
            await this.setBusy();
            await this.setConfirmationMessage('');
            if (!await this.checkAll()) {
                if (this.state.changePassword === '') {
                    await this.setErrorMessage('Please enter a new password.');
                }
                throw('checkAll() failed');
            }
            const result = await this.auth.setPassword(this.state.changePasswordEmail, this.state.changePasswordToken, this.state.changePassword);
            if (result) {
                await this.switchToLogin();
                await this.updateState({email: this.state.changeEmail});
                await this.setConfirmationMessage('Password changed successfully. You may now log in with your new password.');
            } else {
                await this.switchToResetPassword();
                await this.setErrorMessage('Failed to set password. Please try resetting it again.');
            }
        } catch(e) {
            console.error(`can not reset password: ${e}`);
        }
        await this.clearBusy();
    }
}



// WEBPACK FOOTER //
// ./src/components/Landing.tsx